import type { JSX } from 'react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, IntegrationExternalLink, Modal, PasswordInput } from '@feathr/components';

import { VIRTUOUS_API_KEY_URL } from '../virtuousConfig';

interface IProps {
  opened: boolean;
  isConnecting: boolean;
  onClose: () => void;
  /** Resolves `true` once connected. */
  onConnect: (apiKey: string) => Promise<boolean>;
}

function VirtuousConnectModal({
  isConnecting,
  onClose,
  onConnect,
  opened,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState('');
  const isKeyMissing = !apiKey.trim();

  function handleChange(value?: string): void {
    setApiKey(value ?? '');
  }

  function handleClose(): void {
    setApiKey('');
    onClose();
  }

  async function handleConnect(): Promise<void> {
    if (isKeyMissing || isConnecting) {
      return;
    }
    if (await onConnect(apiKey)) {
      setApiKey('');
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleConnect();
    }
  }

  return (
    <Modal
      description={
        <Trans t={t}>
          A Virtuous admin can create an API key in{' '}
          <IntegrationExternalLink href={VIRTUOUS_API_KEY_URL} name={'virtuous-api-key-link'}>
            Virtuous Connect
          </IntegrationExternalLink>
          . Feathr uses it to sync with your Virtuous organization.
        </Trans>
      }
      onClose={handleClose}
      opened={opened}
      rightActions={
        <>
          <Button disabled={isConnecting} name={'virtuous-connect-cancel'} onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={isKeyMissing}
            isLoading={isConnecting}
            name={'virtuous-connect-submit'}
            onClick={handleConnect}
            type={'primary'}
          >
            {t('Connect')}
          </Button>
        </>
      }
      title={t('Connect Virtuous')}
    >
      <PasswordInput
        label={t('Virtuous API key')}
        name={'virtuous-api-key'}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={apiKey}
      />
    </Modal>
  );
}

export default VirtuousConnectModal;
